import { useState } from 'react';
import { useRouter } from 'next/router';
import { List, Collapse, Menu, MenuItem } from '@mui/material';
import { NavListProps } from '../type';
import { NavItemRoot, NavItemSub } from './NavItem';
import { getActive } from '..';

type NavListRootProps = {
  list: NavListProps;
  isCollapse: boolean;
};

export function NavListRoot({ list, isCollapse }: NavListRootProps) {
  const { pathname, asPath, push } = useRouter();

  const active = getActive(list.path, pathname, asPath);

  const [open, setOpen] = useState(active);
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hasChildren = list.children;
  const onListClick = isCollapse
    ? (e: any) => {
        setAnchorEl(e.currentTarget);
        setMenuOpen(!menuOpen);
      }
    : () => setOpen(!open);

  if (hasChildren) {
    return (
      <>
        <NavItemRoot
          item={list}
          isCollapse={isCollapse}
          active={active}
          open={open}
          onOpen={onListClick}
          data-role={`sidebar-nav-root-${list.path}`}
        />

        {!isCollapse ? (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {(list.children || []).map((item) => (
                <NavListSub key={item.title} list={item} />
              ))}
            </List>
          </Collapse>
        ) : (
          <Menu
            open={menuOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={() => {
              setAnchorEl(null);
              setMenuOpen(false);
            }}
            PaperProps={{
              sx: { bgcolor: 'white', padding: '4px', borderRadius: '8px', minWidth: '100px' },
            }}
            MenuListProps={{ sx: { py: 0 } }}
          >
            {(list.children || []).map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => push(item.path)}
                sx={{
                  borderRadius: '8px',
                  fontSize: '14px',
                  textTransform: 'capitalize',
                  bgcolor: getActive(item.path, pathname, asPath) ? '#6F42901F' : 'common.white',
                }}
              >
                {item.title}
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );
  }

  return (
    <NavItemRoot item={list} active={active} isCollapse={isCollapse} sx={{ mx: 0, pl: '12px' }} />
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  list: NavListProps;
  'data-role'?: string;
};

function NavListSub(props: NavListSubProps) {
  const { list } = props;
  const { pathname, asPath } = useRouter();

  const active = getActive(list.path, pathname, asPath);

  const [open, setOpen] = useState(active);

  const hasChildren = list.children;

  if (hasChildren) {
    return (
      <>
        <NavItemSub item={list} onOpen={() => setOpen(!open)} open={open} active={active} />

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 3 }}>
            {(list.children || []).map((item) => (
              <NavItemSub
                key={item.title}
                item={item}
                active={getActive(item.path, pathname, asPath)}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return <NavItemSub item={list} active={active} />;
}
